// Active REI tracker card.
//
// Reverse-chrono list of applications whose re-entry interval hasn't
// expired yet. Click "Signage" to open the print-ready EPA WPS notice in
// a new tab. Critical safety tool — the crew needs to see which zones
// are off-limits at a glance.

import { useEffect, useState } from 'react';
import type { ActiveRei } from '@greeniq/shared';
import { getActiveReis, signageUrl } from '../api/chemical.js';

interface Props {
  courseSlug: string;
}

export function ReiTrackerCard({ courseSlug }: Props) {
  const [reis, setReis] = useState<ActiveRei[] | null>(null);

  async function load() {
    try {
      const r = await getActiveReis(courseSlug);
      setReis(r.reis);
    } catch {
      setReis([]);
    }
  }

  useEffect(() => {
    void load();
    // Refresh every minute so the countdowns stay live.
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [courseSlug]);

  if (reis === null) return null;
  if (reis.length === 0) {
    return (
      <section className="mt-6 p-5 bg-bg-card rounded-lg border border-border">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">
          Active REIs
        </h2>
        <p className="mt-2 text-sm text-fg-muted">
          No active re-entry intervals. The course is clear for normal traffic.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-6 p-5 bg-bg-card rounded-lg border border-[color:var(--color-warning)]/30">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-sm uppercase tracking-wider text-[color:var(--color-warning)] font-semibold">
          Active REIs · {reis.length} zone{reis.length === 1 ? '' : 's'} restricted
        </h2>
      </div>
      <ul className="mt-4 divide-y divide-amber-100">
        {reis.map((r) => (
          <li key={r.applicationId} className="py-3 flex items-start gap-3 flex-wrap">
            <span className={signalBadgeClass(r.signalWord)}>
              {r.signalWord ?? 'CAUTION'}
            </span>
            <div className="flex-1 min-w-[220px]">
              <div className="text-sm font-semibold text-fg">
                {r.productName}
                <span className="ml-2 text-xs text-fg-muted font-normal">
                  · {r.zoneName ?? 'Course-wide'}
                </span>
              </div>
              <div className="text-xs text-fg-muted mt-0.5">
                Applied {relTime(r.appliedAt)} · Re-entry in {formatCountdown(r.remainingHours)}
                {' · '}expires {fmtShort(r.reiExpiresAt)}
              </div>
            </div>
            <a
              href={signageUrl(courseSlug, r.applicationId)}
              target="_blank"
              rel="noreferrer"
              className="min-h-[32px] px-3 inline-flex items-center text-xs rounded-md bg-[color:var(--color-warning)] text-white font-semibold hover:bg-[color:var(--color-warning)]"
            >
              Open signage →
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function signalBadgeClass(signal: string | null): string {
  const base = 'px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider';
  if (signal === 'DANGER') return `${base} bg-error-muted text-[color:var(--color-error)]`;
  if (signal === 'WARNING') return `${base} bg-orange-100 text-orange-900`;
  return `${base} bg-warning-muted text-[color:var(--color-warning)]`;
}

function formatCountdown(hours: number): string {
  if (hours <= 0) return 'now';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${hours.toFixed(1)} h`;
  return `${Math.floor(hours / 24)}d ${Math.round(hours % 24)}h`;
}

function fmtShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diffS = Math.floor((Date.now() - t) / 1000);
  if (diffS < 60) return `${diffS}s ago`;
  if (diffS < 3600) return `${Math.floor(diffS / 60)}m ago`;
  if (diffS < 86400) return `${Math.floor(diffS / 3600)}h ago`;
  return `${Math.floor(diffS / 86400)}d ago`;
}
